import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Service from '../services/Service';
import showToast from '../../../../Shared/Utils/ToastNotification';

const DASH = '—';

const KIT_SHORT_LABELS = {
  mailman: 'Semen',
  tasso: 'Hormone',
};

const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : DASH;

const kitList = (kits) => (kits || []).map((kit) => KIT_SHORT_LABELS[kit] || kit).join(' + ') || DASH;

const oneLineAddress = (address) => {
  if (!address) return DASH;
  const cityLine = [address.city, [address.province_code, address.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [address.name, address.address1, address.address2, cityLine].filter(Boolean).join(', ') || DASH;
};

const LAB_NAMES = {
  mailman: 'MaleMan',
  tasso: 'Tasso',
};

// What happened to the replaced kit on the old order (cancelled with the lab).
const CANCELLATION_LABELS = {
  cancelled: 'Cancelled',
  already_cancelled: 'Already cancelled by lab',
  not_placed: 'Never placed, blocked',
  failed: 'Cancel failed',
};

const cancellationOf = (replacement, kit) => replacement.original_cancellation?.[kit] || null;

const oldKitLines = (replacement) =>
  (replacement.kits || []).map((kit) => {
    const outcome = cancellationOf(replacement, kit);
    return `${KIT_SHORT_LABELS[kit] || kit}: ${outcome ? CANCELLATION_LABELS[outcome.status] || outcome.status : DASH}`;
  });

const replacementErrors = (replacement) =>
  [
    ...(replacement.kits || [])
      .filter((kit) => cancellationOf(replacement, kit)?.status === 'failed')
      .map((kit) => `Cancel ${LAB_NAMES[kit] || kit} (old kit): ${cancellationOf(replacement, kit).error || 'failed'}`),
    replacement.mailman_order_error && `MaleMan: ${replacement.mailman_order_error}`,
    replacement.tasso_order_error && `Tasso: ${replacement.tasso_order_error}`,
  ].filter(Boolean);

// Retry finishes whatever a lab refused: cancelling the old kit or placing the new one.
const needsRetry = (replacement) => {
  const kits = replacement.kits || [];
  const cancelFailed = kits.some((kit) => cancellationOf(replacement, kit)?.status === 'failed');
  const notPlaced = (kits.includes('mailman') && !replacement.mailman_order_id) || (kits.includes('tasso') && !replacement.tasso_order_id);
  return cancelFailed || notPlaced;
};

const RetryButton = ({ replacement, onUpdated }) => {
  const [retrying, setRetrying] = useState(false);
  if (!needsRetry(replacement)) return null;

  const handleRetry = async () => {
    setRetrying(true);
    try {
      const res = await Service.retryOrderReplacement(replacement.id);
      if (res.data?.placement_failed || res.data?.cancellation_failed) {
        showToast('error', 'A lab refused again. See the error.');
      } else {
        showToast('success', 'Replacement completed');
      }
      onUpdated();
    } catch (err) {
      showToast('error', err.response?.data?.detail || 'Retry failed');
    } finally {
      setRetrying(false);
    }
  };

  return (
    <button className="btn btn-primary replacement-retry-btn" onClick={handleRetry} disabled={retrying}>
      {retrying ? 'Retrying…' : 'Retry'}
    </button>
  );
};

RetryButton.propTypes = {
  replacement: PropTypes.object.isRequired,
  onUpdated: PropTypes.func.isRequired,
};

const OrderLink = ({ orderId, label, onOpenOrder }) =>
  onOpenOrder ? (
    <button type="button" className="detail-link replacement-order-link" onClick={() => onOpenOrder(orderId)}>
      {label}
    </button>
  ) : (
    <span>{label}</span>
  );

OrderLink.propTypes = {
  orderId: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onOpenOrder: PropTypes.func,
};

/**
 * Replacement kits ("Re-order Kit") for one order. On a replacement it shows what
 * it replaced and why; on an original it lists every replacement issued for it,
 * including what happened to the old kit it cancelled.
 */
const ReplacementDetails = ({ dbOrder, orderNumberFor, onOpenOrder, onUpdated }) => {
  const replacementOf = dbOrder?.replacement_of;
  const replacements = dbOrder?.replacements || [];
  if (!replacementOf && replacements.length === 0) return null;

  return (
    <>
      {replacementOf && (
        <div className="replacement-banner">
          <h3>Replacement kit #{replacementOf.sequence}</h3>
          <p>
            Sent at no charge for order{' '}
            <OrderLink
              orderId={replacementOf.replaced_order_id || replacementOf.original_order_id}
              label={`#${orderNumberFor(replacementOf.replaced_order_id || replacementOf.original_order_id) || replacementOf.original_order_ref}`}
              onOpenOrder={onOpenOrder}
            />
            .
          </p>
          <div className="detail-row">
            <span className="detail-label">Reason</span>
            <span className="detail-value">{replacementOf.reason_label}</span>
          </div>
          {replacementOf.notes && (
            <div className="detail-row">
              <span className="detail-label">Notes</span>
              <span className="detail-value">{replacementOf.notes}</span>
            </div>
          )}
          <div className="detail-row">
            <span className="detail-label">Kit</span>
            <span className="detail-value">{kitList(replacementOf.kits)}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Old kit</span>
            <span className="detail-value">{oldKitLines(replacementOf).join(', ')}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Issued</span>
            <span className="detail-value">
              {formatDateTime(replacementOf.issued_at)}
              {replacementOf.issued_by && ` by ${replacementOf.issued_by}`}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Shipped to</span>
            <span className="detail-value">{oneLineAddress(replacementOf.shipping_address)}</span>
          </div>
          {replacementErrors(replacementOf).map((message) => (
            <div key={message} className="supplement-error">
              {message}
            </div>
          ))}
          <RetryButton replacement={replacementOf} onUpdated={onUpdated} />
        </div>
      )}

      {replacements.length > 0 && (
        <div className="replacements">
          <h3>Replacement Kits</h3>
          <table className="details-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Issued</th>
                <th>Reason</th>
                <th>Kit</th>
                <th>Old Kit</th>
                <th>New Order</th>
                <th>Lab Orders</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {replacements.map((replacement) => (
                <tr key={replacement.id}>
                  <td>{replacement.sequence}</td>
                  <td>
                    {formatDateTime(replacement.issued_at)}
                    {replacement.issued_by && <div className="replacement-sub">by {replacement.issued_by}</div>}
                  </td>
                  <td>
                    {replacement.reason_label}
                    {replacement.notes && <div className="replacement-sub">{replacement.notes}</div>}
                  </td>
                  <td>{kitList(replacement.kits)}</td>
                  <td>
                    {oldKitLines(replacement).map((line) => (
                      <div key={line}>{line}</div>
                    ))}
                  </td>
                  <td>
                    <OrderLink orderId={replacement.replacement_order_id} label={replacement.replacement_order_ref} onOpenOrder={onOpenOrder} />
                  </td>
                  <td>
                    {(replacement.kits || []).includes('mailman') && <div>MaleMan: {replacement.mailman_order_id || DASH}</div>}
                    {(replacement.kits || []).includes('tasso') && <div>Tasso: {replacement.tasso_order_id || DASH}</div>}
                  </td>
                  <td>
                    {!needsRetry(replacement) && 'Done'}
                    {replacementErrors(replacement).map((message) => (
                      <div key={message} className="shipment-error">
                        {message}
                      </div>
                    ))}
                    <RetryButton replacement={replacement} onUpdated={onUpdated} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

ReplacementDetails.propTypes = {
  dbOrder: PropTypes.object,
  orderNumberFor: PropTypes.func.isRequired,
  onOpenOrder: PropTypes.func,
  onUpdated: PropTypes.func.isRequired,
};

export default ReplacementDetails;
